import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function CANoticePage() {
  useMetaTags({
    title: 'CA Notice at Collection | FindOnlineDegrees',
    description: 'California Consumer Privacy Act notice for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-896">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-897">
        <div className="max-w-4xl mx-auto text-center" data-id="element-898">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-899">
            CA Notice At Collection
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-900">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-901">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-902">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-903">
            <p data-id="element-904">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-905">
              <li data-id="element-906">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-907">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-908">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-909">
            <p data-id="element-910">
              If you are a California resident, the California Consumer Privacy
              Act ("CCPA") requires us to provide you with the following notice
              with information about:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-911">
              <li data-id="element-912">
                the purpose for which we use each category of "personal
                information" (as defined in the CCPA) we collect; and
              </li>
              <li data-id="element-913">
                the categories of third parties to which we (a) disclose such
                personal information for a business purpose, (b) "share"
                personal information for "cross-context behavioral advertising,"
                and/or (c) "sell" such personal information.
              </li>
            </ul>
          </div>

          {/* Categories of Personal Information */}
          <div data-id="element-914">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-915">
              Categories of Personal Information We Collect
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-916">
              <p data-id="element-917">
                The table below describes the categories of personal information
                we have collected from California consumers in the preceding 12
                months, the sources from which we collected it, our business or
                commercial purposes for collecting it, and the categories of
                third parties to whom we disclose, share, or sell that
                information.
              </p>
            </div>

            <div className="overflow-x-auto mt-6" data-id="element-918">
              <table className="min-w-full border border-gray-300" data-id="element-919">
                <thead className="bg-gray-50" data-id="element-920">
                  <tr data-id="element-921">
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-922">
                      Category of Personal Information
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-923">
                      Examples
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-924">
                      Collected?
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-925">
                      Sources
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white" data-id="element-926">
                  <tr data-id="element-927">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-928">
                      <strong data-id="element-929">Identifiers</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-930">
                      Name, email address, phone number, IP address, device ID
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-931">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-932">
                      Directly from you via forms, automatically via cookies
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-933">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-934">
                      <strong data-id="element-935">Personal Information (CA Civil Code)</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-936">
                      Name, contact information, education history
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-937">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-938">
                      Directly from you via forms
                    </td>
                  </tr>
                  <tr data-id="element-939">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-940">
                      <strong data-id="element-941">Commercial Information</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-942">
                      Records of services considered or obtained
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-943">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-944">
                      From your interactions with our services
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-945">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-946">
                      <strong data-id="element-947">Internet/Network Activity</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-948">
                      Browsing history, search history, interaction with website
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-949">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-950">
                      Automatically via cookies and analytics
                    </td>
                  </tr>
                  <tr data-id="element-951">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-952">
                      <strong data-id="element-953">Geolocation Data</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-954">
                      Approximate location based on IP address
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-955">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-956">
                      Automatically via IP address
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-957">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-958">
                      <strong data-id="element-959">Inferences</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-960">
                      Preferences, characteristics, behavior
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-961">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-962">
                      Derived from your activity on our services
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Business Purposes */}
          <div data-id="element-963">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-964">
              Business or Commercial Purposes for Collection
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-965">
              <p data-id="element-966">
                We collect and use personal information for the following
                purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-967">
                <li data-id="element-968">To provide, improve, and administer our services</li>
                <li data-id="element-969">
                  To communicate with you, including responding to inquiries
                </li>
                <li data-id="element-970">To ensure security and fraud prevention</li>
                <li data-id="element-971">To comply with legal obligations</li>
                <li data-id="element-972">To deliver targeted advertising and marketing</li>
                <li data-id="element-973">To analyze and improve our website performance</li>
                <li data-id="element-974">To connect you with educational institutions</li>
              </ul>
            </div>
          </div>

          {/* Disclosure of Personal Information */}
          <div data-id="element-975">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-976">
              Disclosure of Personal Information
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-977">
              <p data-id="element-978">
                We may disclose your personal information to the following
                categories of third parties:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-979">
                <li data-id="element-980">Educational institutions and their partners</li>
                <li data-id="element-981">Service providers (analytics, hosting, marketing)</li>
                <li data-id="element-982">Advertising partners and networks</li>
                <li data-id="element-983">Legal and regulatory authorities when required</li>
                <li data-id="element-984">Business partners in connection with business transfers</li>
              </ul>
            </div>
          </div>

          {/* Your Rights */}
          <div data-id="element-985">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-986">
              Your California Privacy Rights
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-987">
              <p data-id="element-988">As a California resident, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-989">
                <li data-id="element-990">
                  <strong data-id="element-991">Right to Know:</strong> Request disclosure of the
                  categories and specific pieces of personal information we have
                  collected about you.
                </li>
                <li data-id="element-992">
                  <strong data-id="element-993">Right to Delete:</strong> Request deletion of your
                  personal information, subject to certain exceptions.
                </li>
                <li data-id="element-994">
                  <strong data-id="element-995">Right to Correct:</strong> Request correction of
                  inaccurate personal information.
                </li>
                <li data-id="element-996">
                  <strong data-id="element-997">Right to Opt-Out:</strong> Opt out of the "sale" or
                  "sharing" of your personal information for cross-context
                  behavioral advertising.
                </li>
                <li data-id="element-998">
                  <strong data-id="element-999">Right to Limit:</strong> Limit the use and disclosure
                  of sensitive personal information (if applicable).
                </li>
                <li data-id="element-1000">
                  <strong data-id="element-1001">Right to Non-Discrimination:</strong> You will not
                  receive discriminatory treatment for exercising your privacy
                  rights.
                </li>
              </ul>
            </div>
          </div>

          {/* How to Exercise Rights */}
          <div data-id="element-1002">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1003">
              How to Exercise Your Rights
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1004">
              <p data-id="element-1005">
                To exercise your rights under the CCPA, please contact us
                through the information provided on our website. We will verify
                your identity before processing your request and respond within
                the timeframes required by law.
              </p>
            </div>
          </div>

          {/* Contact Us */}
          <div data-id="element-1006">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1007">
              Contact Us
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1008">
              <p data-id="element-1009">
                If you have questions about this notice or wish to exercise your
                California privacy rights, please contact us through the
                information provided on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}